
import { createHttpActions } from '@next/arable-lib/shared';
import { createAction, props } from '@ngrx/store';

import {
  Package,
  OrderLine,
  CropAid,
  SalesOrderPeriod,
  OrderplanPeriodData,
  OrderplanProductData,
  ProductInventoryData,
  DeliveryPeriodMoment,
  PreferredDeliveryPeriods,
  PeriodSystem,
  DeliveryPeriodMomentDisplay,
} from '../overview/models';
import { BemProduct, DigitalProductSubscription, OrderPlanOverviewV2 } from '../overview-v2/models';
import { CropField, CustomerProgress, Employee, CropFieldLinkTypesEnum, ValidationError } from '../shared/models';

export const getLoggedInEmployeeData = createAction(
  '[App] Get logged in employee data',
)

export const setLoggedInEmployeeData = createAction(
  '[App] Set logged in employee data',
  props<{ loggedInEmployee: Employee }>(),
)

export const getOrderPlan = createAction(
  '[App] Get order plan',
)

export const setOrderPlanByPeriods = createAction(
  '[App] Set order plan by periods',
  props<{ orderPlanByPeriods: OrderplanPeriodData[] }>(),
)

export const setOrderPlanByCropAids = createAction(
  '[App] Set order plan by crop aids',
  props<{ orderPlanByCropAids: OrderplanProductData[] }>(),
)

export const getOrderLines = createAction(
  '[App] Get order lines',
)

export const setOrderLines = createAction(
  '[App] Set order lines',
  props<{ gbmOrderLines: OrderLine[] }>(),
)

export const saveOrderLine = createAction(
  '[App] Save order line',
  props<{ orderLine: OrderLine, cropAidId: number }>(),
)

export const saveOrderLines = createAction(
  '[App] Save order lines',
  props<{ orderLines: OrderLine[], toPeriod: SalesOrderPeriod }>(),
)

export const deleteOrderLine = createAction(
  '[App] Delete order line',
  props<{ id: number, noDataRefreshNeeded?: boolean }>(),
)

export const deleteOrderLines = createAction(
  '[App] Delete order lines',
  props<{ ids: number[] }>(),
)

export const editOrderLine = createAction(
  '[App] Edit order line',
  props<{ orderLine: OrderLine, cropAidId: number }>(),
)

export const getCustomerData = createAction(
  '[App] Get customer data',
)

export const getDynamicCustomerOrderPlanData = createAction(
  '[App] Get dynamic customer order plan data',
)

export const selectLocation = createAction(
  '[App] Select location',
  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  (force: boolean = false) => ({ force }),
)

export const getCropAids = createAction(
  '[App] Get crop aids',
)

export const setCropAids = createAction(
  '[App] Set crop aids',
  props<{ cropAids: CropAid[] }>(),
)

export const getProprietaryCropAids = createAction(
  '[App] Get proprietary crop aids',
)

export const setProprietaryCropAids = createAction(
  '[App] Set proprietary crop aids',
  props<{ cropAids: CropAid[] }>(),
)

export const getPackages = createAction(
  '[App] Get packages',
)

export const setPackages = createAction(
  '[App] Set packages',
  props<{ packages: Package[] }>(),
)

export const getSalesOrderPeriods = createAction(
  '[App] Get sales order periods',
)

export const setSalesOrderPeriods = createAction(
  '[App] Set sales order periods',
  props<{ salesOrderPeriods: SalesOrderPeriod[] }>(),
)

export const editCustomerProductInventory = createAction(
  '[App] Edit customer product inventory',
)

export const editCustomerProductInventoryForOverviewV2 = createAction(
  '[App] Edit customer product inventory for overview v2',
)

export const getStockFromTelersdashboard = createAction(
  '[App] Get stock from Telersdashboard',
)

export const getCustomerProductInventory = createAction(
  '[App] Get customer product inventory',
)

export const saveCustomerProductInventory = createAction(
  '[App] Save customer product inventory',
  props<{ productInventories: ProductInventoryData[] }>(),
)

export const setCustomerProductInventory = createAction(
  '[App] Set customer product inventory',
  props<{ productInventories: ProductInventoryData[] }>(),
)

export const optimizeOrderPlan = createAction(
  '[App] Optimize order plan',
)

export const optimizeOrderPlanSuccess = createAction(
  '[App] Optimize order plan success',
  props<{ isOrderPlanOptimized: boolean }>(),
)

export const createOrderReport = createAction(
  '[App] Create order report',
)

export const createCompleteReport = createAction(
  '[App] Create complete report',
)

export const setOrderedId = createAction(
  '[App] Set ordered id',
  props<{ orderedId: number }>(),
)

export const setPreferredDeliveryPeriods = createAction(
  '[App] Set preferred delivery periods',
  props<{ preferredDeliveryPeriods: PreferredDeliveryPeriods }>(),
)

export const setCustomerPreferences = createAction(
  '[App] Set customer preferences',
  props<{ preferredDeliveryPeriods: PreferredDeliveryPeriods, periodSystem: PeriodSystem }>(),
)

export const setPeriodSystem = createAction(
  '[App] Set period system',
  props<{ periodSystem: PeriodSystem }>(),
)

export const getCustomerPreferences = createAction(
  '[App] Get customer preferences',
)

export const editPreferredDeliveryPeriods = createAction(
  '[App] Edit preferred delivery periods',
)

export const editNumberOfPeriods = createAction(
  '[App] Edit number of periods',
)

export const linkCropFields = createAction(
  '[App] Link crop fields',
  props<{ cropFieldLinkType: CropFieldLinkTypesEnum }>(),
)

export const savePreferredDeliveryPeriods = createAction(
  '[App] Save preferred delivery periods',
  props<{ periods: number[] }>(),
)

export const savePeriodSystem = createAction(
  '[App] Save period system',
  props<{ periodSystem: PeriodSystem }>(),
)

export const saveCropFieldLinks = createAction(
  '[App] Save crop field links',
  props<{ cropFields: CropField[] }>(),
)

export const confirmOrder = createAction(
  '[App] Confirm order',
)

export const confirmOrderForOverviewV2 = createAction(
  '[App] Confirm order for overview V2',
)

export const handleConfirmOrderForOverview = createAction(
  '[App] Handle confirm order for overview',
)

export const openMoveProductData = createAction(
  '[App] Open move product data dialog',
  props<{ fromPeriodId: number }>(),
)

export const selectDeliveryPeriodMoment = createAction(
  '[App] Select delivery period moment',
  props<{ deliveryPeriodMoments: DeliveryPeriodMomentDisplay[] }>(),
)

export const saveDeliveryPeriodMoments = createAction(
  '[App] Save delivery period moments',
  props<{ deliveryPeriodMoments: DeliveryPeriodMoment[] }>(),
)

export const selectPeriodsToOrder = createAction(
  '[App] Select periods to order',
)

export const selectDeliveryPreference = createAction(
  '[App] Select delivery preference',
  props<{ periodIds: number[] }>(),
)

export const createOrder = createAction(
  '[App] Create order',
  props<{ periodIds: number[]; deliveryPreference?: string }>(),
)

export const createOrderForDigitalProducts = createAction(
  '[App] Create order for digital products',
)

export const createOrderSuccess = createAction(
  '[App] Create order success',
  props<{ customerId: number, locationId: number }>(),
)

export const resetYear = createAction(
  '[App] Reset year',
  props<{ year?: number }>(),
)

export const setYear = createAction(
  '[App] Set year',
)

export const getDefaultYear = createAction(
  '[App] Get default year',
)

export const setDefaultYear = createAction(
  '[App] Set default year',
  props<{ defaultYear: number }>(),
)

export const setAvailableYears = createAction(
  '[App] Set available years',
  props<{ availableYears: number[] }>(),
)

export const openFieldAdvice = createAction(
  '[App] Open field advice',
)

export const openFieldPlan = createAction(
  '[App] Open field plan',
)

export const getCropFields = createAction(
  '[App] Get crop fields',
)

export const setCropFields = createAction(
  '[App] Set crop fields',
  props<{ cropFields: CropField[] }>(),
)

export const getShipTosWithOrders = createAction(
  '[App] Get ship tos with orders',
)

export const setShipTosWithOrders = createAction(
  '[App] Set ship tos with orders',
  props<{ shipToIds: number[] }>(),
)

export const loadCustomerProgress = createAction(
  '[App] Load customer progress',
)

export const loadCustomerProgressSuccess = createAction(
  '[App] Load customer progress success',
  props<{ customerProgress: CustomerProgress }>(),
)

export const toggleCustomerProgressOrderplan = createAction(
  '[App] Toggle customer progress orderplan',
)

export const getOrderPlanOverviewV2List = createAction(
  '[App] Get order plan overview V2 list',
)

export const getOrderPlanOverviewListSuccess = createAction(
  '[App] Get order plan overview V2 list success',
  props<{ orderPlanOverviewV2List: OrderPlanOverviewV2[] }>(),
)

export const openDigitalProductsDialog = createAction(
  '[App] Open digital products dialog',
)

export const getDigitalProductsSubscriptions = createAction(
  '[App] Get digital products subscriptions',
  props<{ openDialog: boolean }>(),
)

export const getDigitalProductsSubscriptionsSuccess = createAction(
  '[App] Get digital products subscriptions success',
  props<{ requisites: DigitalProductSubscription[] }>(),
)

export const saveDigitalProductsSubscriptions = createAction(
  '[App] Save digital products subscriptions',
  props<{ requisites: DigitalProductSubscription[] }>(),
)

export const saveDigitalProductsSubscriptionsSuccess = createAction(
  '[App] Save digital products subscriptions success',
)

export const validationErrorHappened = createAction(
  '[App] An Validation Error happened',
  props<{ errors: ValidationError[] }>(),
)

export const  [loadOrderedBemProducts, loadOrderedBemProductsSuccess, loadOrderedBemProductsFailed] = createHttpActions<
  { cropYear: number; },
  { products: BemProduct[] },
  undefined
>('Load BEM Products');
